import React from 'react';
import { MessageCircle, X } from 'lucide-react';
import ExternalHtmlFrame from './ui/ExternalHtmlFrame';
import { sanitizeExternalHtml } from '../utils/sanitizeExternalHtml';
import { useI18n } from '../utils/i18n';
import '../styles/TeamsMessageDetail.css';

const TeamsMessageDetail = ({ message, onClose }) => {
  // #329: Diese Ansicht war die einzige Komponente ganz ohne t() -- und damit
  // auch fuer den i18n-Check unsichtbar, der nur findet, was durch t() laeuft.
  const { t, getLocale } = useI18n();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Vorher fest 'de-DE': das Datum blieb deutsch, auch wenn die Oberflaeche
    // auf Englisch stand.
    return date.toLocaleString(getLocale(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'high':
      case 'urgent': return 'var(--color-wichtigkeit-hoch)';
      case 'normal': return 'var(--color-wichtigkeit-normal)';
      default: return 'var(--color-wichtigkeit-normal)';
    }
  };

  const getImportanceText = (importance) => {
    switch (importance) {
      case 'high': return t('teams.importance.high', 'Hoch');
      case 'urgent': return t('teams.importance.urgent', 'Dringend');
      case 'normal': return t('teams.importance.normal', 'Normal');
      default: return t('teams.importance.normal', 'Normal');
    }
  };

  const getChatTypeText = (chatType) => {
    switch (chatType) {
      case 'oneOnOne': return t('teams.chatType.oneOnOne', 'Direktnachricht');
      case 'group': return t('teams.chatType.group', 'Gruppenchat');
      default: return t('teams.chatType.unknown', 'Unbekannt');
    }
  };

  // Microsoft Graph hostedContents-URLs benötigen einen Bearer-Token und lassen sich nicht
  // direkt als <img src> laden. Sie werden hier auf unsere strukturierte Server-Route
  // (/api/teams/messages/:messageId/hosted-contents/:hostedContentId) umgeschrieben,
  // die zunächst den DB-Cache prüft und nur bei Cache-Miss live von Graph nachlädt –
  // analog zum Flow bei E-Mail-Inline-Bildern.
  const rewriteHostedContentUrls = (html) => {
    if (typeof html !== 'string' || !html) return html;
    const hostedRegex = /https:\/\/graph\.microsoft\.com\/[^"'\s<>]*?\/hostedContents\/[^"'\s<>]+?\/\$value/gi;
    return html.replace(hostedRegex, (match) => {
      try {
        const parsed = new URL(match);
        const pathname = parsed.pathname;
        const chatMatch = pathname.match(/^\/[^/]+\/chats\/([^/]+)\/messages\/([^/]+)\/hostedContents\/([^/]+)\/\$value\/?$/i);
        if (chatMatch) {
          const [, chatId, messageId, hostedContentId] = chatMatch.map(decodeURIComponent);
          return `/api/teams/messages/${encodeURIComponent(messageId)}/hosted-contents/${encodeURIComponent(hostedContentId)}?chatId=${encodeURIComponent(chatId)}`;
        }
        const channelMatch = pathname.match(/^\/[^/]+\/teams\/([^/]+)\/channels\/([^/]+)\/messages\/([^/]+)\/hostedContents\/([^/]+)\/\$value\/?$/i);
        if (channelMatch) {
          const [, teamId, channelId, messageId, hostedContentId] = channelMatch.map(decodeURIComponent);
          return `/api/teams/messages/${encodeURIComponent(messageId)}/hosted-contents/${encodeURIComponent(hostedContentId)}?teamId=${encodeURIComponent(teamId)}&channelId=${encodeURIComponent(channelId)}`;
        }
      } catch (_) { /* fall through to url-param fallback */ }
      // Fallback: die Legacy-Route akzeptiert die komplette URL.
      return `/api/teams/hosted-content?url=${encodeURIComponent(match)}`;
    });
  };

  const renderMessageBody = (body) => {
    if (!body) return <div className="message-body-empty">{t('teams.detail.noContent', 'Kein Inhalt verfügbar')}</div>;
    
    // Handle different body formats:
    // 1. Microsoft Graph API format: { content: "...", contentType: "HTML" }
    // 2. Direct string format
    let bodyContent;
    
    if (typeof body === 'string') {
      bodyContent = body;
    } else if (typeof body === 'object' && body.content) {
      bodyContent = body.content;
    } else {
      console.warn('Unexpected body format:', body);
      return <div className="message-body-empty">{t('teams.detail.unknownFormat', 'Unbekanntes Nachrichtenformat')}</div>;
    }

    bodyContent = rewriteHostedContentUrls(bodyContent);
    bodyContent = sanitizeExternalHtml(bodyContent);

    // #285: Teams-HTML ist fremder Inhalt wie eine Mail und laeuft deshalb im
    // selben abgeschotteten Rahmen (#273). Der Rahmen gehoert IN den
    // scrollenden Kasten, nicht an seine Stelle — ein <iframe> bekommt durch
    // overflow-y:auto keine Bildlaufleiste.
    //
    // minHeight kleiner als bei Mails: Teams-Nachrichten sind meist ein bis
    // zwei Zeilen, ein 120px-Rahmen waere fast nur Leerraum.
    return (
      <div className="message-body-content">
        <ExternalHtmlFrame
          html={bodyContent}
          title={t('teams.detail.frameTitle', 'Teams-Nachrichteninhalt')}
          minHeight={40}
        />
      </div>
    );
  };

  const getSenderName = (message) => {
    if (message.from?.user?.displayName) {
      return message.from.user.displayName;
    }
    if (message.fromName) {
      return message.fromName;
    }
    // #329: Hier stand eine selbstgebaute Sprachweiche ueber navigator.language --
    // sie kannte nur Deutsch und Englisch und ignorierte die im Programm
    // eingestellte Sprache. Das kann t() besser.
    return t('teams.detail.unknownSender', 'Unbekannter Absender');
  };

  if (!message) return null;

  return (
    <div className="teams-message-detail-overlay">
      <div className="teams-message-detail-container">
        <div className="teams-message-detail-header">
          <div className="message-header-info">
            <MessageCircle size={20} />
            <h2>{t('teams.detail.title', 'Teams-Nachricht — Details')}</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="teams-message-detail-content">
          <div className="message-meta-section">
            <div className="message-meta-grid">
              <div className="meta-row">
                <div className="meta-item">
                  <span className="meta-label">{t('teams.detail.from', 'Von:')}</span>
                  <span className="meta-value">{getSenderName(message)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">{t('teams.detail.chat', 'Chat:')}</span>
                  <span className="meta-value">
                    {message.chatTopic && message.chatTopic !== 'Direct Chat' 
                      ? message.chatTopic 
                      : getChatTypeText(message.chatType)}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">{t('teams.detail.sent', 'Gesendet:')}</span>
                  <span className="meta-value">
                    {formatDate(message.createdDateTime)}
                  </span>
                </div>
              </div>
            </div>

            <div className="message-subject">
              <h3>
                {message.subject || t('teams.detail.subjectFallback', 'Teams-Nachricht')}
                {message.importance && message.importance !== 'normal' && (
                  <span className="importance-badge" style={{ color: getImportanceColor(message.importance) }}>
                    {getImportanceText(message.importance)}
                  </span>
                )}
              </h3>
            </div>

            <div className="message-flags">
              <span className="flag-badge chat-type">
                {getChatTypeText(message.chatType)}
              </span>
              {message.importance && message.importance !== 'normal' && (
                <span className="flag-badge importance" style={{ color: getImportanceColor(message.importance) }}>
                  {getImportanceText(message.importance)}
                </span>
              )}
              {message.messageType && (
                <span className="flag-badge message-type">
                  {message.messageType}
                </span>
              )}
            </div>
          </div>

          <div className="message-body-section">
            <h4>{t('teams.detail.contentHeading', 'Nachrichteninhalt')}</h4>
            {renderMessageBody(message.body)}
            
            {/* Fallback für plain text body */}
            {!message.body && message.plainTextBody && (
              <div className="message-body-content plain-text">
                <pre>{message.plainTextBody}</pre>
              </div>
            )}
          </div>

          {/* Additional metadata */}
          <div className="message-additional-info">
            {message.lastModifiedDateTime && message.lastModifiedDateTime !== message.createdDateTime && (
              <div className="info-item">
                <span className="info-label">{t('teams.detail.edited', 'Bearbeitet:')}</span>
                <span className="info-value">{formatDate(message.lastModifiedDateTime)}</span>
              </div>
            )}
            {message.replyToId && (
              <div className="info-item">
                <span className="info-label">{t('teams.detail.replyTo', 'Antwort auf:')}</span>
                <span className="info-value">{t('teams.detail.messageRef', 'Nachricht #{{id}}', { id: message.replyToId.substring(0, 8) })}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamsMessageDetail; 